import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import { useAskQuestion } from '@/api/hooks/questionAnswerHooks';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { MessageData, messageResolver } from '@/lib/validations/MessageSchema';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import TextInputField from '../inputs/TextInputField';
import Text from '../Text';

interface AskQuestionFormProps {
  npoId: string;
  programId?: string;
  volunteerId?: string;
  amenityId?: string;
  messageOnSuccess?: string;
  titleOnSuccess?: string;
  onSuccess?: () => void;
}

const defaultValues: MessageData = {
  message: ''
};

const AskQuestionForm = ({
  npoId,
  programId,
  volunteerId,
  amenityId,
  messageOnSuccess,
  titleOnSuccess,
  onSuccess
}: AskQuestionFormProps) => {
  const { mutateAsync: askQuestion, isPending } = useAskQuestion();
  const { showError, showSuccess } = useEndFlowModal();

  const {
    control,
    handleSubmit,
    formState: { isValid, errors }
  } = useForm<MessageData>({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: messageResolver
  });

  const onSubmit: SubmitHandler<MessageData> = async (data) => {
    askQuestion(
      {
        question: data.message,
        fromEmail: 'contact@brokenshepherds.com', //Hardcoded to avoid npos having user emails
        nonProfitOrgId: npoId,
        programId: programId,
        volunteerId: volunteerId,
        amenityId: amenityId
      },
      {
        onSuccess: (data) => {
          showSuccess({ title: titleOnSuccess ?? i18n.t('end_flow.success_title'), message: messageOnSuccess ?? data });
          onSuccess && onSuccess();
        },
        onError: (error) => {
          console.error('Error asking a question', error);
          showError({});
        }
      }
    );
  };

  return (
    <View style={style.formContainer}>
      <TextInputField
        name="message"
        control={control}
        placeholder={i18n.t('program_detail.write_message_placeholder')}
        multiline
        minHeight={120}
        maxHeight={120}
      />

      {errors.root && <Text style={style.errorText}>{errors?.root?.message}</Text>}
      <View style={style.buttonContainer}>
        <BrandedButton loading={isPending} style={style.btnStyle} onPress={handleSubmit(onSubmit)} disabled={!isValid}>
          {i18n.t('mix.submit')}
        </BrandedButton>
      </View>
    </View>
  );
};

const style = StyleSheet.create({
  formContainer: {
    flex: 1,
    flexDirection: 'column',
    gap: 28
  },
  buttonContainer: {
    flex: 1,
    justifyContent: 'flex-end'
  },
  btnStyle: {
    width: '100%'
  },
  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  }
});

export default AskQuestionForm;
